import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, Users, DollarSign, Cpu, TrendingUp, Wallet, Download,
  Search, Star, Trash2, ShieldAlert, Lock, AlertTriangle, ChevronRight, X, Coins,
} from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

const usd = (n) => `$${Number(n || 0).toLocaleString("es", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const usd4 = (n) => `$${Number(n || 0).toFixed(4)}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("es", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString("es", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const STATUS_STYLE = {
  activo: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  gratis: "bg-white/5 text-[#94A3B8] border-white/10",
  cancelado: "bg-[#FF334B]/15 text-[#FF334B] border-[#FF334B]/30",
};

const TABS = [
  { id: "resumen", label: "Resumen", icon: TrendingUp },
  { id: "clientes", label: "Clientes", icon: Users },
  { id: "pagos", label: "Pagos", icon: DollarSign },
  { id: "costos", label: "Costos Fal", icon: Cpu },
  { id: "comunidad", label: "Comunidad", icon: Star },
];

function Stat({ icon: Icon, label, value, accent = "#FF334B", sub }) {
  return (
    <div className="mfp-card p-4" data-testid={`stat-${label}`}>
      <div className="flex items-center gap-2 text-[#64748B]">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        <span className="text-xs font-mono uppercase tracking-wider">{label}</span>
      </div>
      <div className="mt-2 text-2xl font-head font-bold">{value}</div>
      {sub && <div className="text-xs text-[#64748B] mt-0.5">{sub}</div>}
    </div>
  );
}

function SeriesTable({ title, rows }) {
  return (
    <div className="mfp-card p-4" data-testid={`series-${title}`}>
      <div className="mono-label mb-3">{title}</div>
      {(!rows || rows.length === 0) ? (
        <p className="text-sm text-[#64748B] py-4 text-center">Sin datos todavía.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[#64748B] text-xs uppercase tracking-wide">
                <th className="text-left py-1.5 font-medium">Periodo</th>
                <th className="text-right font-medium">Ingreso</th>
                <th className="text-right font-medium">Fal</th>
                <th className="text-right font-medium">Stripe</th>
                <th className="text-right font-medium">Ganancia</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.period} className="border-t border-white/5">
                  <td className="py-1.5 font-mono text-xs text-[#94A3B8]">{r.period}</td>
                  <td className="text-right">{usd(r.revenue)}</td>
                  <td className="text-right text-[#FF9F2E]">{usd4(r.fal_cost)}</td>
                  <td className="text-right text-[#64748B]">{usd(r.stripe_fees)}</td>
                  <td className={`text-right font-semibold ${r.profit >= 0 ? "text-emerald-400" : "text-[#FF334B]"}`}>{usd(r.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ResumenTab({ range }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    const params = {};
    if (range.from) params.date_from = range.from;
    if (range.to) params.date_to = range.to;
    api.get("/admin/overview", { params }).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [range.from, range.to]);
  if (loading || !data) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 spin text-[#FF334B] mx-auto" /></div>;
  const c = data.customers;
  const t = data.totals;
  return (
    <div className="space-y-5">
      {data.fal_low_alert && (
        <div data-testid="fal-low-alert" className="mfp-card p-4 border border-[#FF334B]/40 bg-[#FF334B]/10 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-[#FF334B] mt-0.5 shrink-0" />
          <div>
            <div className="font-head font-semibold text-[#FF334B]">Saldo de Fal bajo</div>
            <div className="text-sm text-[#94A3B8]">Tu cuenta de Fal tiene <b>{usd(data.fal_balance)}</b> (menos de $10). Recarga en fal.ai/dashboard/billing para no interrumpir las generaciones.</div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Users} label="Clientes" value={c.total} accent="#00F0FF" sub={`${c.active} activos · ${c.free} gratis · ${c.canceled} cancelados`} />
        <Stat icon={DollarSign} label="Ingresos" value={usd(t.revenue)} accent="#34D399" sub={`${t.payments_count} pagos`} />
        <Stat icon={Cpu} label="Gasto Fal" value={usd(t.fal_cost)} accent="#FF9F2E" sub={`${t.fal_calls} llamadas`} />
        <Stat icon={TrendingUp} label="Ganancia" value={usd(t.profit)} accent="#FF334B" sub={`Stripe ${usd(t.stripe_fees)}`} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Stat icon={Wallet} label="Saldo Fal" value={data.fal_balance == null ? "—" : usd(data.fal_balance)} accent={data.fal_low_alert ? "#FF334B" : "#34D399"} sub={data.byok_enabled ? "cuenta propia (BYOK)" : "clave universal"} />
        <div className="mfp-card p-4">
          <div className="mono-label mb-2">Suscriptores por plan</div>
          {Object.keys(c.by_plan || {}).length === 0 ? (
            <p className="text-sm text-[#64748B]">Sin suscriptores activos.</p>
          ) : Object.entries(c.by_plan).map(([pid, n]) => (
            <div key={pid} className="flex justify-between text-sm py-1">
              <span className="text-[#94A3B8]">{data.plan_names[pid] || pid}</span>
              <span className="font-semibold">{n}</span>
            </div>
          ))}
        </div>
        <div className="mfp-card p-4">
          <div className="mono-label mb-2">Comunidad</div>
          <div className="flex justify-between text-sm py-1"><span className="text-[#94A3B8]">Publicaciones</span><span className="font-semibold">{data.community.published}</span></div>
          <div className="flex justify-between text-sm py-1"><span className="text-[#94A3B8]">Destacadas</span><span className="font-semibold text-[#FF9F2E]">{data.community.featured}</span></div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <SeriesTable title="Diario (30 días)" rows={data.series.daily} />
        <SeriesTable title="Semanal (12 semanas)" rows={data.series.weekly} />
        <SeriesTable title="Mensual (12 meses)" rows={data.series.monthly} />
      </div>
    </div>
  );
}

function FilterBar({ q, setQ, extra, onExport, exporting }) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <div className="relative flex-1 min-w-[200px]">
        <Search className="w-4 h-4 text-[#64748B] absolute left-3 top-1/2 -translate-y-1/2" />
        <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por correo…"
          className="w-full bg-[#12131C] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm focus:border-[#FF334B] outline-none" />
      </div>
      {extra}
      {onExport && (
        <button data-testid="admin-export-btn" onClick={onExport} disabled={exporting}
          className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg bg-[#FF334B] text-white hover:bg-[#e02a40] transition-colors disabled:opacity-60">
          {exporting ? <Loader2 className="w-4 h-4 spin" /> : <Download className="w-4 h-4" />} Exportar CSV
        </button>
      )}
    </div>
  );
}

function ClientesTab({ onOpen, doExport, range }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (plan) params.plan = plan;
      if (status) params.status = status;
      if (range.from) params.date_from = range.from;
      if (range.to) params.date_to = range.to;
      const r = await api.get("/admin/customers", { params });
      setRows(r.data.items);
    } catch { toast.error("No se pudieron cargar los clientes"); }
    finally { setLoading(false); }
  }, [q, plan, status, range.from, range.to]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const exp = async () => {
    setExporting(true);
    await doExport("customers", { q, plan, status, date_from: range.from, date_to: range.to });
    setExporting(false);
  };

  const selectCls = "bg-[#12131C] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#FF334B] outline-none";
  return (
    <div>
      <FilterBar q={q} setQ={setQ} onExport={exp} exporting={exporting} extra={
        <>
          <select data-testid="filter-plan" value={plan} onChange={(e) => setPlan(e.target.value)} className={selectCls}>
            <option value="">Todos los planes</option>
            <option value="free">Gratis</option>
            <option value="basico">Básico</option>
            <option value="creador">Creador</option>
            <option value="estudio">Estudio</option>
          </select>
          <select data-testid="filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls}>
            <option value="">Todos los estados</option>
            <option value="activo">Activos</option>
            <option value="gratis">Gratis</option>
            <option value="cancelado">Cancelados</option>
          </select>
        </>
      } />
      {loading ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 spin text-[#FF334B] mx-auto" /></div> : (
        <div className="mfp-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[#64748B] text-xs uppercase tracking-wide border-b border-white/8">
                <th className="text-left p-3 font-medium">Cliente</th>
                <th className="text-left font-medium">Plan / Estado</th>
                <th className="text-left font-medium">Registro</th>
                <th className="text-right font-medium">Créditos (rest.)</th>
                <th className="text-right font-medium">Ingreso</th>
                <th className="text-right font-medium">Fal</th>
                <th className="text-right font-medium">Ganancia</th>
                <th className="text-right font-medium">Margen</th>
                <th></th>
              </tr>
            </thead>
            <tbody data-testid="customers-table">
              {rows.length === 0 ? (
                <tr><td colSpan={9} className="p-6 text-center text-[#64748B]">Sin clientes con estos filtros.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.user_id} data-testid={`customer-row-${r.user_id}`}
                  onClick={() => onOpen(r.user_id)}
                  className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors">
                  <td className="p-3">
                    <div className="font-medium truncate max-w-[180px]">{r.name || "—"}</div>
                    <div className="text-xs text-[#64748B] truncate max-w-[180px]">{r.email}</div>
                  </td>
                  <td>
                    <span className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${STATUS_STYLE[r.status]}`}>{r.status}</span>
                    <div className="text-xs text-[#94A3B8] mt-0.5">{r.plan_name}</div>
                  </td>
                  <td className="text-xs text-[#94A3B8]">{fmtDate(r.created_at)}</td>
                  <td className="text-right">
                    <span className="font-semibold text-[#00F0FF]">{r.credits.remaining}</span>
                    <div className="text-[10px] text-[#64748B]">−{r.credits.consumed} · ↩{r.credits.refunded}</div>
                  </td>
                  <td className="text-right">{usd(r.revenue)}</td>
                  <td className="text-right text-[#FF9F2E]">{usd4(r.fal_cost)}</td>
                  <td className={`text-right font-semibold ${r.profit >= 0 ? "text-emerald-400" : "text-[#FF334B]"}`}>{usd(r.profit)}</td>
                  <td className="text-right text-xs text-[#94A3B8]">{r.margin == null ? "—" : `${(r.margin * 100).toFixed(0)}%`}</td>
                  <td className="pr-3"><ChevronRight className="w-4 h-4 text-[#64748B]" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CustomerDrawer({ userId, onClose }) {
  const [d, setD] = useState(null);
  const [bump, setBump] = useState(0);
  const [nAdd, setNAdd] = useState("");
  const [reason, setReason] = useState("");
  const [granting, setGranting] = useState(false);
  useEffect(() => {
    api.get(`/admin/customers/${userId}`).then((r) => setD(r.data)).catch(() => toast.error("No se pudo cargar el cliente"));
  }, [userId, bump]);
  const grant = async () => {
    const credits = parseInt(nAdd, 10);
    if (!credits || credits <= 0) { toast.error("Escribe una cantidad válida (mayor que 0)"); return; }
    setGranting(true);
    try {
      const r = await api.post(`/admin/customers/${userId}/credits`, { credits, reason: reason || undefined });
      toast.success(`+${r.data.granted} créditos · saldo ${r.data.balance}`);
      setNAdd(""); setReason(""); setBump((b) => b + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudieron sumar los créditos"); }
    finally { setGranting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="customer-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg h-full bg-[#0C0D14] border-l border-white/10 overflow-y-auto p-5">
        <button data-testid="drawer-close" onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full border border-white/12 flex items-center justify-center hover:border-[#FF334B] hover:text-[#FF334B]"><X className="w-4 h-4" /></button>
        {!d ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 spin text-[#FF334B] mx-auto" /></div> : (
          <>
            <div className="mono-label">Cliente</div>
            <div className="text-xl font-head font-bold mt-1">{d.user.name || "—"}</div>
            <div className="text-sm text-[#64748B]">{d.user.email}</div>
            <div className="flex flex-wrap gap-2 mt-3 text-xs">
              <span className={`px-2 py-0.5 rounded-full border ${STATUS_STYLE[d.wallet.status]}`}>{d.wallet.status}</span>
              <span className="px-2 py-0.5 rounded-full border border-white/10 text-[#94A3B8]">{d.wallet.plan_name}</span>
              <span className="px-2 py-0.5 rounded-full border border-white/10 text-[#00F0FF]">{d.wallet.balance} créditos</span>
              <span className="px-2 py-0.5 rounded-full border border-white/10 text-[#94A3B8]">Registro {fmtDate(d.user.created_at)}</span>
            </div>

            <div className="mt-5 rounded-xl border border-[#FF334B]/25 bg-[#FF334B]/5 p-3" data-testid="grant-credits-box">
              <div className="mono-label mb-2 flex items-center gap-1.5"><Coins className="w-3.5 h-3.5 text-[#FF9F2E]" /> Sumar créditos (cortesía / ajuste)</div>
              <div className="flex flex-wrap items-center gap-2">
                <input data-testid="grant-credits-amount" type="number" min="1" value={nAdd} onChange={(e) => setNAdd(e.target.value)}
                  placeholder="Cantidad" className="w-28 bg-[#12131C] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#FF334B] outline-none" />
                <input data-testid="grant-credits-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="Motivo (opcional)" className="flex-1 min-w-[140px] bg-[#12131C] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#FF334B] outline-none" />
                <button data-testid="grant-credits-btn" onClick={grant} disabled={granting}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg bg-[#FF334B] text-white hover:bg-[#e02a40] transition-colors disabled:opacity-60">
                  {granting ? <Loader2 className="w-4 h-4 spin" /> : <Coins className="w-4 h-4" />} Sumar
                </button>
              </div>
              <p className="text-[11px] text-[#64748B] mt-1.5">Se añaden al saldo permanente (packs) y quedan registrados en el historial de créditos.</p>
            </div>

            <div className="mono-label mt-6 mb-2">Pagos ({d.payments.length})</div>
            {d.payments.length === 0 ? <p className="text-sm text-[#64748B]">Sin pagos.</p> : (
              <div className="space-y-1.5">
                {d.payments.map((p, i) => (
                  <div key={i} className="flex justify-between items-center text-sm bg-white/5 rounded-lg px-3 py-2">
                    <div><span className="font-medium">{p.lookup_key}</span> <span className="text-xs text-[#64748B]">· {p.mode}</span></div>
                    <div className="text-right"><div>{usd(p.amount)}</div><div className={`text-[10px] ${p.credited ? "text-emerald-400" : "text-[#FF9F2E]"}`}>{p.credited ? "pagado" : p.payment_status}</div></div>
                  </div>
                ))}
              </div>
            )}

            <div className="mono-label mt-6 mb-2">Costo Fal por trabajo ({d.fal_costs.length})</div>
            {d.fal_costs.length === 0 ? <p className="text-sm text-[#64748B]">Aún sin llamadas a Fal registradas.</p> : (
              <div className="space-y-1.5">
                {d.fal_costs.map((f, i) => (
                  <div key={i} className="flex justify-between items-center text-sm bg-white/5 rounded-lg px-3 py-2">
                    <div className="truncate max-w-[260px]"><span className="font-medium">{f.operation}</span> <span className="text-xs text-[#64748B]">· {f.project_title || "—"}</span><div className="text-[10px] text-[#64748B] truncate">{f.endpoint}{f.seconds ? ` · ${f.seconds}s` : ""} · {f.engine}</div></div>
                    <div className="text-[#FF9F2E] font-mono text-xs">{usd4(f.cost_usd)}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="mono-label mt-6 mb-2">Historial de créditos ({d.credit_ledger.length})</div>
            <div className="space-y-1.5">
              {d.credit_ledger.slice(0, 60).map((c, i) => (
                <div key={i} className="flex justify-between items-center text-sm bg-white/5 rounded-lg px-3 py-2">
                  <div className="truncate max-w-[260px]"><span className="font-medium">{c.note || c.kind}</span><div className="text-[10px] text-[#64748B] truncate">{c.project_title || c.kind} · {fmtDateTime(c.created_at)}</div></div>
                  <div className={`font-semibold ${c.type === "charge" ? "text-[#FF334B]" : "text-emerald-400"}`}>{c.type === "charge" ? "−" : "+"}{c.credits}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PagosTab({ doExport, range }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      if (range.from) params.date_from = range.from;
      if (range.to) params.date_to = range.to;
      const r = await api.get("/admin/payments", { params });
      setRows(r.data.items);
    } catch { toast.error("No se pudieron cargar los pagos"); }
    finally { setLoading(false); }
  }, [q, status, range.from, range.to]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const exp = async () => { setExporting(true); await doExport("payments", { q, status, date_from: range.from, date_to: range.to }); setExporting(false); };
  return (
    <div>
      <FilterBar q={q} setQ={setQ} onExport={exp} exporting={exporting} extra={
        <select data-testid="filter-pay-status" value={status} onChange={(e) => setStatus(e.target.value)}
          className="bg-[#12131C] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#FF334B] outline-none">
          <option value="">Todos</option>
          <option value="paid">Pagados</option>
          <option value="pending">Pendientes</option>
        </select>
      } />
      {loading ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 spin text-[#FF334B] mx-auto" /></div> : (
        <div className="mfp-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[#64748B] text-xs uppercase tracking-wide border-b border-white/8">
              <th className="text-left p-3 font-medium">Cliente</th><th className="text-left font-medium">Producto</th>
              <th className="text-left font-medium">Tipo</th><th className="text-right font-medium">Monto</th>
              <th className="text-left font-medium pl-4">Estado</th><th className="text-left font-medium">Fecha</th></tr></thead>
            <tbody data-testid="payments-table">
              {rows.length === 0 ? <tr><td colSpan={6} className="p-6 text-center text-[#64748B]">Sin pagos.</td></tr> :
                rows.map((p, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="p-3 text-xs">{p.email}</td>
                    <td className="font-medium">{p.lookup_key}</td>
                    <td className="text-[#94A3B8]">{p.mode === "subscription" ? "Suscripción" : "Pack"}</td>
                    <td className="text-right">{usd(p.amount)}</td>
                    <td className="pl-4"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${p.credited ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-[#FF9F2E]/15 text-[#FF9F2E] border-[#FF9F2E]/30"}`}>{p.credited ? "pagado" : p.payment_status}</span></td>
                    <td className="text-xs text-[#94A3B8]">{fmtDateTime(p.created_at)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CostosTab({ doExport, range }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [engine, setEngine] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (engine) params.engine = engine;
      if (range.from) params.date_from = range.from;
      if (range.to) params.date_to = range.to;
      const r = await api.get("/admin/fal-costs", { params });
      setRows(r.data.items);
    } catch { toast.error("No se pudieron cargar los costos"); }
    finally { setLoading(false); }
  }, [q, engine, range.from, range.to]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const exp = async () => { setExporting(true); await doExport("falcosts", { q, date_from: range.from, date_to: range.to }); setExporting(false); };
  return (
    <div>
      <FilterBar q={q} setQ={setQ} onExport={exp} exporting={exporting} extra={
        <select data-testid="filter-engine" value={engine} onChange={(e) => setEngine(e.target.value)}
          className="bg-[#12131C] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#FF334B] outline-none">
          <option value="">Todos los motores</option>
          <option value="byok">Cuenta propia (BYOK)</option>
          <option value="proxy">Clave universal</option>
        </select>
      } />
      {loading ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 spin text-[#FF334B] mx-auto" /></div> : (
        <div className="mfp-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[#64748B] text-xs uppercase tracking-wide border-b border-white/8">
              <th className="text-left p-3 font-medium">Cliente</th><th className="text-left font-medium">Trabajo</th>
              <th className="text-left font-medium">Modelo</th><th className="text-left font-medium">Op.</th>
              <th className="text-right font-medium">Dur.</th><th className="text-right font-medium">Costo</th>
              <th className="text-left font-medium pl-3">Motor</th><th className="text-left font-medium">Fecha</th></tr></thead>
            <tbody data-testid="costs-table">
              {rows.length === 0 ? <tr><td colSpan={8} className="p-6 text-center text-[#64748B]">Aún sin llamadas a Fal (se registran desde ahora, por llamada).</td></tr> :
                rows.map((r, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="p-3 text-xs">{r.email}</td>
                    <td className="text-xs truncate max-w-[140px]">{r.project_title || "—"}</td>
                    <td className="text-[10px] font-mono text-[#94A3B8] truncate max-w-[180px]">{r.endpoint}</td>
                    <td className="text-[#94A3B8]">{r.operation}</td>
                    <td className="text-right text-xs">{r.seconds ? `${r.seconds}s` : "—"}</td>
                    <td className="text-right text-[#FF9F2E] font-mono">{usd4(r.cost_usd)}</td>
                    <td className="pl-3 text-xs">{r.engine === "byok" ? "propia" : "universal"}</td>
                    <td className="text-xs text-[#94A3B8]">{fmtDateTime(r.created_at)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ComunidadTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/admin/community"); setRows(r.data.items); }
    catch { toast.error("No se pudo cargar la comunidad"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (item, action) => {
    if (action === "remove" && !window.confirm(`¿Retirar "${item.title}" de la comunidad?`)) return;
    try {
      await api.post(`/admin/community/${item.id}/${action}`);
      toast.success(action === "remove" ? "Publicación retirada" : action === "feature" ? "Destacada" : "Quitado de destacados");
      load();
    } catch { toast.error("Acción no permitida"); }
  };
  if (loading) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 spin text-[#FF334B] mx-auto" /></div>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-community-grid">
      {rows.length === 0 ? <p className="text-[#64748B] col-span-full text-center py-8">No hay publicaciones en la comunidad.</p> :
        rows.map((item) => (
          <div key={item.id} className="mfp-card p-3" data-testid={`admin-community-${item.id}`}>
            <div className="font-head font-semibold text-sm truncate">{item.title}</div>
            <div className="text-xs text-[#64748B] truncate">{item.creator_name} · {item.owner_email}</div>
            <div className="flex items-center gap-1.5 mt-1 text-[11px] text-[#94A3B8]">
              <span className="px-1.5 py-0.5 rounded bg-white/5">{item.category}</span>
              {item.featured && <span className="inline-flex items-center gap-1 text-[#FF9F2E]"><Star className="w-3 h-3" /> destacado</span>}
            </div>
            <div className="flex gap-2 mt-3">
              <button data-testid={`admin-feature-${item.id}`} onClick={() => act(item, item.featured ? "unfeature" : "feature")}
                className="flex-1 text-[11px] font-semibold px-2 py-1.5 rounded-lg border border-[#FF9F2E]/50 text-[#FF9F2E] hover:bg-[#FF9F2E]/10 inline-flex items-center justify-center gap-1">
                <Star className="w-3 h-3" /> {item.featured ? "Quitar" : "Destacar"}
              </button>
              <button data-testid={`admin-remove-${item.id}`} onClick={() => act(item, "remove")}
                className="flex-1 text-[11px] font-semibold px-2 py-1.5 rounded-lg border border-[#FF334B]/50 text-[#FF334B] hover:bg-[#FF334B]/10 inline-flex items-center justify-center gap-1">
                <Trash2 className="w-3 h-3" /> Retirar
              </button>
            </div>
          </div>
        ))}
    </div>
  );
}

export default function AdminPanel() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [tab, setTab] = useState("resumen");
  const [openUser, setOpenUser] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const range = { from, to };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    api.get("/me/admin").then((r) => {
      setIsAdmin(r.data.admin);
      setChecking(false);
    }).catch(() => setChecking(false));
  }, [user, authLoading, navigate]);

  const doExport = async (type, params) => {
    try {
      const r = await api.get("/admin/export", { params: { type, ...params }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `metafora_${type}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch { toast.error("No se pudo exportar"); }
  };

  if (authLoading || checking) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-7 h-7 text-[#FF334B] spin" /></div>;
  }
  if (!isAdmin) {
    return (
      <div className="min-h-screen grain flex flex-col items-center justify-center gap-4 px-4 text-center" data-testid="admin-forbidden">
        <ShieldAlert className="w-12 h-12 text-[#FF334B]" />
        <div className="text-xl font-head font-bold">Acceso restringido</div>
        <p className="text-[#94A3B8] max-w-sm">Este panel es solo para el administrador de Metáfora Play.</p>
        <button onClick={() => navigate("/dashboard")} className="text-sm font-semibold px-4 py-2 rounded-lg bg-[#FF334B] text-white">Volver al estudio</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen grain relative pb-16">
      <header className="glass sticky top-0 z-30 border-b border-white/8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button data-testid="back-to-studio" onClick={() => navigate("/dashboard")} className="w-9 h-9 rounded-full border border-white/12 flex items-center justify-center hover:border-[#FF334B] hover:text-[#FF334B]"><ArrowLeft className="w-4 h-4" /></button>
            <Logo size="text-lg" />
            <span className="hidden sm:inline text-[0.6rem] font-mono uppercase tracking-widest text-[#FF334B]">Panel de control</span>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full border border-white/12 text-[#94A3B8]">
            <Lock className="w-3.5 h-3.5" /> Panel admin
          </span>
        </div>
        <div className="border-t border-white/8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto">
            {TABS.map((t) => (
              <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`relative flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${tab === t.id ? "text-white" : "text-[#64748B] hover:text-[#94A3B8]"}`}>
                <t.icon className="w-4 h-4" /> {t.label}
                {tab === t.id && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-[#FF334B] rounded-full" />}
              </button>
            ))}
          </div>
        </div>
        <div className="border-t border-white/8 bg-[#0A0B11]/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex flex-wrap items-center gap-2 text-xs">
            <span className="text-[#64748B] font-mono uppercase tracking-wider mr-1">Rango</span>
            <input type="date" data-testid="range-from" value={from} onChange={(e) => setFrom(e.target.value)}
              className="bg-[#12131C] border border-white/10 rounded-lg px-2 py-1.5 text-[#94A3B8] focus:border-[#FF334B] outline-none" />
            <span className="text-[#64748B]">→</span>
            <input type="date" data-testid="range-to" value={to} onChange={(e) => setTo(e.target.value)}
              className="bg-[#12131C] border border-white/10 rounded-lg px-2 py-1.5 text-[#94A3B8] focus:border-[#FF334B] outline-none" />
            {(from || to) && (
              <button data-testid="range-clear" onClick={() => { setFrom(""); setTo(""); }}
                className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg border border-white/10 text-[#94A3B8] hover:border-[#FF334B] hover:text-[#FF334B]">
                <X className="w-3 h-3" /> Limpiar
              </button>
            )}
            <span className="text-[#64748B] hidden sm:inline">· afecta Resumen, Clientes, Pagos y Costos Fal</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {tab === "resumen" && <ResumenTab range={range} />}
        {tab === "clientes" && <ClientesTab onOpen={setOpenUser} doExport={doExport} range={range} />}
        {tab === "pagos" && <PagosTab doExport={doExport} range={range} />}
        {tab === "costos" && <CostosTab doExport={doExport} range={range} />}
        {tab === "comunidad" && <ComunidadTab />}
      </main>

      {openUser && <CustomerDrawer userId={openUser} onClose={() => setOpenUser(null)} />}
    </div>
  );
}
